// admin-panel/src/pages/ProjectForm.jsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../lib/api';
import { ArrowLeft, Upload, Trash2, Save, Plus, X, Video, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';

export default function ProjectForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!id;
  const fileRef = useRef();
  const [activeTab, setActiveTab] = useState('details');

  const [form, setForm] = useState({
    title: '', category_id: '', short_description: '', description: '',
    client_name: '', location: '', area_sqft: '', completion_date: '',
    budget_range: '', status: 'draft', featured: false,
    meta_title: '', meta_description: '',
  });
  const [thumb, setThumb] = useState(null);
  const [thumbPreview, setThumbPreview] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('');

  const { data: cats = [] } = useQuery({ queryKey: ['categories'], queryFn: projectsApi.categories });

  const { data: project } = useQuery({
    queryKey: ['project', id],
    queryFn: () => projectsApi.get(id),
    enabled: isEdit,
  });

  useEffect(() => {
    if (project) {
      setForm({
        title: project.title || '',
        category_id: project.category?.id || '',
        short_description: project.short_description || '',
        description: project.description || '',
        client_name: project.client_name || '',
        location: project.location || '',
        area_sqft: project.area_sqft || '',
        completion_date: project.completion_date || '',
        budget_range: project.budget_range || '',
        status: project.status || 'draft',
        featured: project.featured || false,
        meta_title: project.meta_title || '',
        meta_description: project.meta_description || '',
      });
      if (project.thumbnail) setThumbPreview(project.thumbnail);
    }
  }, [project]);

  const saveMut = useMutation({
    mutationFn: (data) => isEdit ? projectsApi.update(id, data) : projectsApi.create(data),
    onSuccess: (res) => {
      qc.invalidateQueries(['admin-projects']);
      toast.success(isEdit ? 'Project updated!' : 'Project created!');
      if (!isEdit) navigate(`/projects/${res.project?.id}/edit`);
    },
    onError: (e) => toast.error(e.message || 'Save failed'),
  });

  const imgMut = useMutation({
    mutationFn: ({ pid, fd }) => projectsApi.uploadImages(pid, fd),
    onSuccess: () => { qc.invalidateQueries(['project', id]); toast.success('Images uploaded!'); },
    onError: (e) => toast.error(e.message || 'Upload failed'),
  });

  const delImgMut = useMutation({
    mutationFn: ({ pid, iid }) => projectsApi.deleteImage(pid, iid),
    onSuccess: () => { qc.invalidateQueries(['project', id]); toast.success('Image removed'); },
  });

  const addVideoMut = useMutation({
    mutationFn: ({ pid, data }) => projectsApi.addVideo(pid, data),
    onSuccess: () => { qc.invalidateQueries(['project', id]); toast.success('Video added'); setVideoUrl(''); setVideoTitle(''); },
  });

  const delVideoMut = useMutation({
    mutationFn: ({ pid, vid }) => projectsApi.deleteVideo(pid, vid),
    onSuccess: () => { qc.invalidateQueries(['project', id]); toast.success('Video removed'); },
  });

  function handleSave() {
    if (!form.title.trim()) return toast.error('Title is required');
    const fd = new FormData();
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (thumb) fd.append('thumbnail', thumb);
    saveMut.mutate(fd);
  }

  function handleThumb(e) {
    const file = e.target.files[0];
    if (!file) return;
    setThumb(file);
    setThumbPreview(URL.createObjectURL(file));
  }

  function handleImageUpload(e) {
    const files = Array.from(e.target.files);
    if (!files.length || !id) return;
    const fd = new FormData();
    files.forEach(f => fd.append('images[]', f));
    imgMut.mutate({ pid: id, fd });
  }

  function addVideo() {
    if (!videoUrl.trim() || !id) return;
    const type = videoUrl.includes('youtube') ? 'youtube' : videoUrl.includes('vimeo') ? 'vimeo' : 'youtube';
    addVideoMut.mutate({ pid: id, data: { video_url: videoUrl, video_type: type, title: videoTitle } });
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const TABS = [
    { key: 'details', label: 'Details' },
    { key: 'media',   label: `Media${project?.images?.length ? ` (${project.images.length})` : ''}` },
    { key: 'seo',     label: 'SEO' },
  ];

  return (
    <div className="space-y-5 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/projects')} className="p-2 rounded-xl bg-[#151515] border border-white/5 text-[#888] hover:text-white transition-all">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{isEdit ? 'Edit Project' : 'New Project'}</h1>
          <p className="text-sm text-[#555] mt-0.5">{isEdit ? `Editing: ${project?.title || '…'}` : 'Create a new portfolio project'}</p>
        </div>
        <div className="flex gap-3">
          <select value={form.status} onChange={e => set('status', e.target.value)}
            className="bg-[#151515] border border-white/5 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30">
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <button onClick={handleSave} disabled={saveMut.isPending}
            className="flex items-center gap-2 bg-gradient-to-r from-[#8B5E3C] to-[#D4AF37] text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
            <Save size={15} /> {saveMut.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#151515] border border-white/5 rounded-xl p-1 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === t.key ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20' : 'text-[#666] hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Details Tab */}
      {activeTab === 'details' && (
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-4">
              <div>
                <label className="block text-xs text-[#666] mb-1.5">Project Title *</label>
                <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Luxury Master Bedroom, Adajan Surat"
                  className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-[#666] mb-1.5">Category</label>
                  <select value={form.category_id} onChange={e => set('category_id', e.target.value)}
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30">
                    <option value="">Select category</option>
                    {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#666] mb-1.5">Location</label>
                  <input value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Adajan, Surat"
                    className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#666] mb-1.5">Short Description</label>
                <textarea value={form.short_description} onChange={e => set('short_description', e.target.value)} rows={2} placeholder="Brief summary shown in cards…"
                  className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none" />
              </div>
              <div>
                <label className="block text-xs text-[#666] mb-1.5">Full Description</label>
                <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={5} placeholder="Detailed project description…"
                  className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none" />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {[
                  { k: 'area_sqft', l: 'Area (sqft)', p: '250' },
                  { k: 'budget_range', l: 'Budget Range', p: '₹1.5L – ₹2L' },
                  { k: 'client_name', l: 'Client Name', p: 'Mr. Sharma' },
                ].map(({ k, l, p }) => (
                  <div key={k}>
                    <label className="block text-xs text-[#666] mb-1.5">{l}</label>
                    <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={p}
                      className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
                  </div>
                ))}
              </div>
              <div>
                <label className="block text-xs text-[#666] mb-1.5">Completion Date</label>
                <input type="date" value={form.completion_date} onChange={e => set('completion_date', e.target.value)}
                  className="bg-[#1A1A1A] border border-white/5 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#D4AF37]/30" />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {/* Thumbnail */}
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
              <label className="block text-xs text-[#666] mb-3">Thumbnail Image</label>
              {thumbPreview ? (
                <div className="relative group">
                  <img src={thumbPreview} className="w-full aspect-[4/3] object-cover rounded-xl" />
                  <button onClick={() => { setThumb(null); setThumbPreview(null); }}
                    className="absolute top-2 right-2 p-1.5 bg-red-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center aspect-[4/3] border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors bg-[#1A1A1A]">
                  <Upload size={24} className="text-[#444] mb-2" />
                  <span className="text-xs text-[#555]">Click to upload thumbnail</span>
                  <input type="file" accept="image/*" onChange={handleThumb} className="hidden" />
                </label>
              )}
            </div>

            {/* Featured */}
            <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className="text-sm font-medium text-white">Featured Project</div>
                  <div className="text-xs text-[#555] mt-0.5">Show on homepage</div>
                </div>
                <div onClick={() => set('featured', !form.featured)}
                  className={`w-11 h-6 rounded-full transition-all relative cursor-pointer ${form.featured ? 'bg-[#D4AF37]' : 'bg-white/10'}`}>
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.featured ? 'left-6' : 'left-1'}`} />
                </div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Media Tab */}
      {activeTab === 'media' && (
        <div className="space-y-5">
          {!isEdit && (
            <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4 text-sm text-[#D4AF37]">
              Save the project first before adding images.
            </div>
          )}

          {isEdit && (
            <>
              {/* Upload zone */}
              <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4">Project Images</h3>
                <label className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-[#D4AF37]/30 transition-colors bg-[#1A1A1A] mb-4">
                  {imgMut.isPending ? (
                    <div className="w-5 h-5 border-2 border-[#D4AF37]/30 border-t-[#D4AF37] rounded-full animate-spin" />
                  ) : (
                    <>
                      <Upload size={22} className="text-[#444] mb-1.5" />
                      <span className="text-xs text-[#555]">Click to upload images (multiple allowed)</span>
                    </>
                  )}
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" disabled={imgMut.isPending} />
                </label>

                {project?.images?.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {project.images.map(img => (
                      <div key={img.id} className="relative group aspect-square rounded-xl overflow-hidden bg-[#1A1A1A]">
                        <img src={img.url} className="w-full h-full object-cover" />
                        {img.is_primary && (
                          <div className="absolute top-1 left-1 text-[8px] bg-[#D4AF37] text-black px-1.5 py-0.5 rounded font-bold">MAIN</div>
                        )}
                        <button onClick={() => delImgMut.mutate({ pid: id, iid: img.id })}
                          className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Trash2 size={16} className="text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-[#555] text-sm py-4">No images yet.</p>
                )}
              </div>

              {/* Videos */}
              <div className="bg-[#151515] border border-white/5 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-white mb-4">Videos</h3>
                <div className="flex gap-3 mb-4">
                  <input value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="YouTube / Vimeo URL…"
                    className="flex-1 bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
                  <input value={videoTitle} onChange={e => setVideoTitle(e.target.value)} placeholder="Video title"
                    className="w-40 bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
                  <button onClick={addVideo} disabled={!videoUrl || addVideoMut.isPending}
                    className="px-4 py-2.5 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] rounded-xl text-sm font-medium hover:bg-[#D4AF37]/20 disabled:opacity-40 transition-all">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="space-y-2">
                  {project?.videos?.map(v => (
                    <div key={v.id} className="flex items-center gap-3 p-3 bg-[#1A1A1A] rounded-xl border border-white/5">
                      <Video size={16} className="text-[#D4AF37] shrink-0" />
                      <span className="text-sm text-white flex-1 truncate">{v.title || v.url}</span>
                      <span className="text-xs text-[#555] uppercase">{v.type}</span>
                      <button onClick={() => delVideoMut.mutate({ pid: id, vid: v.id })} className="text-[#555] hover:text-red-400 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                  {!project?.videos?.length && <p className="text-[#555] text-sm text-center py-3">No videos added.</p>}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* SEO Tab */}
      {activeTab === 'seo' && (
        <div className="bg-[#151515] border border-white/5 rounded-xl p-5 space-y-4 max-w-2xl">
          <h3 className="text-sm font-semibold text-white">SEO Settings</h3>
          {[
            { k: 'meta_title', l: 'Meta Title', p: 'Page title for search engines (50–60 chars)' },
            { k: 'meta_description', l: 'Meta Description', p: 'Brief description for search results (150–160 chars)', rows: 3 },
          ].map(({ k, l, p, rows }) => (
            <div key={k}>
              <label className="block text-xs text-[#666] mb-1.5">{l}</label>
              {rows ? (
                <textarea value={form[k]} onChange={e => set(k, e.target.value)} rows={rows} placeholder={p}
                  className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30 resize-none" />
              ) : (
                <input value={form[k]} onChange={e => set(k, e.target.value)} placeholder={p}
                  className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#444] focus:outline-none focus:border-[#D4AF37]/30" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
